import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { AppNav } from '@/components/AppNav';
import { fetchCalendar, type CalendarDay } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { useLocale, useT } from '@/i18n/context';
import type { MessageKey } from '@/i18n/types';
import { monthLabel } from '@/lib/months';

/**
 * Monday-first, which matches most of the world outside the US and both of the
 * languages the app ships in. The order is fixed; only the abbreviations move
 * with the locale.
 */
const WEEKDAYS: MessageKey[] = [
  'calendar.mon',
  'calendar.tue',
  'calendar.wed',
  'calendar.thu',
  'calendar.fri',
  'calendar.sat',
  'calendar.sun',
];

/** Sunday is 0 in JS; shift so Monday starts the row. */
function leadingBlanks(firstDate: string): number {
  const [year, month, day] = firstDate.split('-').map(Number);

  if (!year || !month || !day) {
    return 0;
  }

  return (new Date(year, month - 1, day).getDay() + 6) % 7;
}

/** Months since year zero, so stepping never has to special-case December. */
function shiftMonth(today: string, offset: number): { year: number; month: number } {
  const index = Number(today.slice(0, 4)) * 12 + (Number(today.slice(5, 7)) - 1) + offset;

  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

export function CalendarPage() {
  const { data: user } = useSession();
  const { locale } = useLocale();
  const t = useT();

  // An offset from the user's own current month rather than an absolute month.
  // Seeding absolute state would have to guess before the session arrives, and
  // the browser's UTC date is the wrong month for anyone near midnight.
  const [offset, setOffset] = useState(0);

  const today = user?.today ?? null;
  const cursor = today ? shiftMonth(today, offset) : null;

  const { data, isPending, error } = useQuery({
    queryKey: ['calendar', cursor?.year, cursor?.month],
    queryFn: () => fetchCalendar(cursor?.year ?? 0, cursor?.month ?? 0),
    enabled: cursor !== null,
  });

  const step = (delta: number) => setOffset((current) => current + delta);

  const key = cursor ? `${cursor.year}-${String(cursor.month).padStart(2, '0')}` : '';
  const days = data?.days ?? [];

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col gap-6 p-6 pb-24">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">
            {monthLabel(key, locale)}
          </h1>
          <p className="text-sm opacity-60">
            {data
              ? t('calendar.filled', { filled: data.filled, n: days.length })
              : t('calendar.subtitle')}
          </p>
        </div>

        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label={t('calendar.previous_month')}
            className="rounded-full bg-current/10 px-3 py-2"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => step(1)}
            aria-label={t('calendar.next_month')}
            className="rounded-full bg-current/10 px-3 py-2"
          >
            ›
          </button>
        </div>
      </header>

      {isPending && <p className="text-sm opacity-60">{t('common.loading')}</p>}

      {error && (
        <p className="rounded-lg bg-red-500/10 p-4 text-sm text-red-500">
          {t('calendar.load_failed', { message: error.message })}
        </p>
      )}

      {days.length > 0 && (
        <section>
          <div className="grid grid-cols-7 gap-1.5 pb-2 text-center text-xs opacity-50">
            {WEEKDAYS.map((day) => (
              <span key={day}>{t(day)}</span>
            ))}
          </div>

          <ol className="grid grid-cols-7 gap-1.5">
            {Array.from({ length: leadingBlanks(days[0]?.date ?? '') }, (_, index) => (
              <li key={`blank-${index}`} aria-hidden="true" />
            ))}

            {days.map((day) => (
              <li key={day.date}>
                <Day day={day} isToday={day.date === today} />
              </li>
            ))}
          </ol>
        </section>
      )}

      <AppNav />
    </main>
  );
}

function Day({ day, isToday }: { day: CalendarDay; isToday: boolean }) {
  const number = Number(day.date.slice(8));
  const ring = isToday ? 'ring-2 ring-current/40' : '';

  if (day.photo?.thumb_url) {
    return (
      <figure
        className={`relative aspect-square overflow-hidden rounded-lg ${ring}`}
        title={day.date}
      >
        <img
          src={day.photo.thumb_url}
          alt={day.date}
          loading="lazy"
          decoding="async"
          className="size-full object-cover"
        />
        <figcaption className="absolute inset-x-0 bottom-0 bg-black/40 text-center text-[10px] text-white">
          {number}
        </figcaption>
      </figure>
    );
  }

  // A day still to come is drawn as empty rather than missed — marking it as a
  // gap would make the current month always look half failed.
  const missed = !day.is_future;

  return (
    <div
      title={day.date}
      className={`grid aspect-square place-items-center rounded-lg text-xs ${ring} ${
        missed ? 'bg-current/5 opacity-60' : 'opacity-25'
      }`}
    >
      {missed ? <span className="size-1.5 rounded-full bg-current" /> : number}
    </div>
  );
}
